/**
 * アカウント管理APIクライアント
 */

import { AuthenticatedApiClient } from '../client';
import type {
  AccountDetail,
  AccountFeatures,
  AccountSettingsNext,
  UpdateAccountSettingsRequest,
  UpdateAccountSettingsNextRequest,
  UpdateAccountFeaturesRequest,
  UpdateAccountContractRequest,
  FirstLevelProductGroup,
  ProductGroupType,
  TableRecordCount,
  MiseTask,
} from '@/lib/types/mise/account';
import type { MiseSellerGroupNode, MiseSellerNode } from '@/lib/types/mise/seller';

/**
 * アカウント詳細を取得（認証付き）
 */
export async function getAccountByIdAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<AccountDetail | null> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const account = await client.get<AccountDetail>(`/mise/accounts/${accountId}`);
    return account;
  } catch (error) {
    console.error(`Failed to fetch account with id ${accountId} (authenticated):`, error);
    throw error;
  }
}

/**
 * アカウント設定を更新（認証付き）
 */
export async function updateAccountSettings(
  accountId: string,
  request: UpdateAccountSettingsRequest,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.put(`/mise/accounts/${accountId}/settings`, request);
  } catch (error) {
    console.error(`Failed to update account settings for ${accountId}:`, error);
    throw error;
  }
}

/**
 * アカウント設定 next（予約中のバッチ集計条件）を取得（認証付き）
 *
 * 予約が存在しない場合は null を返す（API 側も 200 + null を返す）。
 */
export async function getAccountSettingsNextAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<AccountSettingsNext | null> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.get<AccountSettingsNext | null>(
      `/mise/accounts/${accountId}/settings/next`,
    );
  } catch (error) {
    console.error(`Failed to fetch account settings next for ${accountId}:`, error);
    throw error;
  }
}

/**
 * アカウント設定 next を更新（認証付き）
 *
 * Some の列のみ UPSERT（部分マージ）。翌日バッチで current に反映される。
 */
export async function updateAccountSettingsNext(
  accountId: string,
  request: UpdateAccountSettingsNextRequest,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.put(`/mise/accounts/${accountId}/settings/next`, request);
  } catch (error) {
    console.error(`Failed to update account settings next for ${accountId}:`, error);
    throw error;
  }
}

/**
 * 店舗一覧を取得（認証付き、snapshot_hot.sellers）
 */
export async function getSellersAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<MiseSellerNode[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const sellers = await client.get<MiseSellerNode[]>(`/mise/accounts/${accountId}/sellers`);
    return sellers;
  } catch (error) {
    console.error(`Failed to fetch sellers for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * 店舗グループ一覧を取得（認証付き、snapshot_hot.seller_groups）
 *
 * 階層フィールド（parent_id / level / sort_order）を含む。フロント側でツリーに再構築する。
 */
export async function getSellerGroupsAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<MiseSellerGroupNode[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const sellerGroups = await client.get<MiseSellerGroupNode[]>(
      `/mise/accounts/${accountId}/seller-groups`,
    );
    return sellerGroups;
  } catch (error) {
    console.error(`Failed to fetch seller groups for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * 商品グループタイプ一覧を取得（認証付き、snapshot_hot.product_group_types）
 */
export async function getProductGroupTypesAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<ProductGroupType[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const productGroupTypes = await client.get<ProductGroupType[]>(
      `/mise/accounts/${accountId}/product-group-types`,
    );
    return productGroupTypes;
  } catch (error) {
    console.error(`Failed to fetch product group types for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * テーブルレコード件数を取得（認証付き）
 */
export async function getTableRecordCountsAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<TableRecordCount[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const counts = await client.get<TableRecordCount[]>(
      `/mise/accounts/${accountId}/table-record-counts`,
    );
    return counts;
  } catch (error) {
    console.error(`Failed to fetch table record counts for ${accountId}:`, error);
    throw error;
  }
}

/**
 * アカウントのバッチタスク一覧を取得（認証付き）
 */
export async function getAccountTasksAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<MiseTask[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const tasks = await client.get<MiseTask[]>(`/mise/accounts/${accountId}/tasks`);
    return tasks;
  } catch (error) {
    console.error(`Failed to fetch tasks for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * アカウント機能設定を取得（認証付き）
 */
export async function getAccountFeaturesAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<AccountFeatures> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.get<AccountFeatures>(`/mise/accounts/${accountId}/features`);
  } catch (error) {
    console.error(`Failed to fetch features for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * アカウント機能設定を更新（認証付き）
 */
export async function updateAccountFeaturesAuthenticated(
  accountId: string,
  request: UpdateAccountFeaturesRequest,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.put(`/mise/accounts/${accountId}/features`, request);
  } catch (error) {
    console.error(`Failed to update features for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * アカウント契約状態を更新（認証付き）
 */
export async function updateAccountContractAuthenticated(
  accountId: string,
  request: UpdateAccountContractRequest,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.put(`/mise/accounts/${accountId}/contract`, request);
  } catch (error) {
    console.error(`Failed to update contract for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * アカウントデータをZIP形式でエクスポート（認証付き）
 */
export async function exportAccountDataAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<Blob> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const blob = await client.getBlob(`/mise/accounts/${accountId}/export`);
    return blob;
  } catch (error) {
    console.error(`Failed to export account data for ${accountId}:`, error);
    throw error;
  }
}

/**
 * 集計期間の選択肢を取得（認証付き）
 */
export async function getAggregationPeriodsAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<string[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const periods = await client.get<string[]>(`/mise/accounts/${accountId}/aggregation-periods`);
    return periods;
  } catch (error) {
    console.error(`Failed to fetch aggregation periods for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * 第1階層の商品グループ一覧を取得（認証付き）
 */
export async function getFirstLevelProductGroupsAuthenticated(
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<FirstLevelProductGroup[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const groups = await client.get<FirstLevelProductGroup[]>(
      `/mise/accounts/${accountId}/first-level-product-groups`,
    );
    return groups;
  } catch (error) {
    console.error(`Failed to fetch first level product groups for account ${accountId}:`, error);
    throw error;
  }
}
